import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";

import {
  DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
  formatSlotForDisplay,
  generateServiceSlotsFromAvailability,
  filterSlotsAgainstBlocks,
  getDayAvailabilityForDate,
  groupBlockedTimesByDate,
  mergeWeeklyAvailability,
  toISODateString,
  combineDateAndTime,
  getBookedDurationMinutes,
  parseCalendarDay,
  timeStringToMinutes,
} from "../../../utils/availability";
import { buildGoogleCalendarLink } from "../../../utils/calendarLinks";
import { DEFAULT_TIME_ZONE, bookingTimeZoneNotice } from "../../../utils/timeZones";
import { contrastRatio, hexToRgba, isHexColor } from "../../../utils/themeColor";
import { getAvatarInitial, resolveAvatarUrl } from "../../../utils/profileAvatar";
import { getCancellationReasonOptions } from "../../../utils/cancellationReasons";
import {
  formatBookingAnswerValue,
  getBookingAnswerAttachments,
} from "../../../utils/bookingAnswers";
import {
  evaluateCancellationRefundEligibility,
  getCancellationPolicyOption,
  normalizeCancellationPolicy,
} from "../../../constants/cancellationPolicy";

import "../../customer/booking-form.css";
import "react-datepicker/dist/react-datepicker.css";
import "./BookingManagePage.css";

const formatCurrency = (value) => {
  if (value === undefined || value === null) return "$0.00";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
};

const toCents = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
};

// What the client has paid and what is left for the appointment, for the line
// under "You're booked". A card payment records what it took in amountPaid; a
// free reservation and pay-in-person have taken nothing, so the whole total is
// left. Any other state (a payment still due or processing) gives "", and the
// page shows the payment status instead.
const describePaidAndDue = ({ paymentStatus, amountPaid, amountDue }, total) => {
  if (paymentStatus !== "paid" && paymentStatus !== "due_in_person") return "";
  const paidCents = toCents(amountPaid);
  const leftCents = Math.max((toCents(total) || toCents(amountDue)) - paidCents, 0);
  const paid = paidCents > 0 ? `Paid ${formatCurrency(paidCents / 100)}` : "";
  const left =
    leftCents > 0 ? `${formatCurrency(leftCents / 100)} due at your appointment` : "";
  return [paid, left].filter(Boolean).join(" · ") || "Nothing to pay";
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatBookingDate = (value) => {
  const date = parseCalendarDay(value);
  if (!date) return "Date TBD";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatTimeRange = (slot) => {
  return formatSlotForDisplay(slot);
};

const normalizeRefundStatus = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

// A location on one line: the street (saved as `fullAddress`), then city, state
// and ZIP. Older records saved the whole address as the street; those are used
// as they are rather than given the city twice.
const normalizeLocationString = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "object") {
    const readPart = (key) => (typeof value[key] === "string" ? value[key].trim() : "");
    const street = readPart("fullAddress");
    const zip = readPart("zip");
    const cityState = [readPart("city"), readPart("state")].filter(Boolean).join(", ");
    const locality = [cityState, zip].filter(Boolean).join(" ");
    if (!street) {
      return locality || readPart("serviceArea");
    }
    const streetText = street.toLowerCase();
    const streetHasLocality = zip
      ? streetText.includes(zip.toLowerCase())
      : Boolean(cityState) && streetText.includes(cityState.toLowerCase());
    return locality && !streetHasLocality ? `${street}, ${locality}` : street;
  }
  return "";
};

const normalizeFullAddressOnly = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "object") {
    return typeof value.fullAddress === "string" ? value.fullAddress.trim() : "";
  }
  return "";
};

const formatAnswerValue = (value) => formatBookingAnswerValue(value);

const getBookingQuestionAnswerAttachmentsForDisplay = (answer) =>
  getBookingAnswerAttachments(answer)
    .map((attachment) =>
      typeof attachment?.url === "string" ? attachment.url.trim() : "",
    )
    .filter(Boolean);

const paymentCopy = {
  paid: "Paid",
  processing: "Payment processing",
  requires_payment: "Payment due",
  due_in_person: "Due at appointment",
  failed: "Payment failed",
  partially_refunded: "Partly refunded",
  refunded: "Refunded",
  disputed: "Payment disputed",
};

const statusCopy = {
  accepted: "Confirmed",
  pending: "Pending",
  completed: "Completed",
  cancelled: "Cancelled",
  declined: "Declined",
};

const normalizeSlotKey = (slot) => `${slot.start}-${slot.end}`;
const DAY_SLOTS_ERROR_MESSAGE =
  "We couldn't load times for that day. Please choose another date.";
const NO_DAY_SLOTS = { date: "", slots: [], error: "" };
// The server rejects a cancellation note longer than this.
const CANCEL_NOTE_MAX_LENGTH = 500;
const EMPTY_FEEDBACK = { type: "", message: "" };
const DEFAULT_THEME_BACKGROUND = "#eef2ff";
const DEFAULT_THEME_TEXT = "#0f172a";
const LIGHT_TEXT = "#ffffff";

const resolveReadableTextColor = (backgroundColor, preferredTextColor) => {
  const background = isHexColor(backgroundColor) ? backgroundColor : DEFAULT_THEME_BACKGROUND;
  const candidates = [];
  if (isHexColor(preferredTextColor)) {
    candidates.push(preferredTextColor);
  }
  candidates.push(DEFAULT_THEME_TEXT, LIGHT_TEXT);

  let bestColor = candidates[0];
  let bestRatio = contrastRatio(background, bestColor);

  candidates.forEach((candidate) => {
    const ratio = contrastRatio(background, candidate);
    if (ratio > bestRatio) {
      bestColor = candidate;
      bestRatio = ratio;
    }
  });

  return bestColor;
};

const BookingManagePage = () => {
  const { token } = useParams();
  const location = useLocation();
  const queryAction = new URLSearchParams(location.search).get("action");
  // The booking form hands over with this state, so the page opens on "You're
  // booked" rather than on the tools. The emailed link carries none.
  const arrivedFromBooking = location.state?.bookingConfirmed === true;

  const [booking, setBooking] = useState(null);
  const [bookingError, setBookingError] = useState("");
  const [loadingBooking, setLoadingBooking] = useState(true);

  const [professionalProfile, setProfessionalProfile] = useState(null);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [weeklyAvailability, setWeeklyAvailability] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState([]);
  // The chosen reschedule day's own times, and which day they are for.
  const [daySlots, setDaySlots] = useState(NO_DAY_SLOTS);
  const [daySlotsLoading, setDaySlotsLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [blockedTimes, setBlockedTimes] = useState([]);

  const [activePanel, setActivePanel] = useState(() => {
    if (queryAction === "reschedule" || queryAction === "cancel") {
      return queryAction;
    }
    return "";
  });

  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleDateObj, setRescheduleDateObj] = useState(null);
  const [rescheduleSlot, setRescheduleSlot] = useState("");
  const [rescheduleFeedback, setRescheduleFeedback] = useState({ type: "", message: "" });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const rescheduleRef = useRef(null);
  const cancelRef = useRef(null);

  const [cancelReasonCode, setCancelReasonCode] = useState("");
  const [cancelReasonNote, setCancelReasonNote] = useState("");
  const [hasConfirmedCancel, setHasConfirmedCancel] = useState(false);
  const [cancelFeedback, setCancelFeedback] = useState({ type: "", message: "" });
  const [isCancelling, setIsCancelling] = useState(false);

  // The outcome of a reschedule or cancellation that went through. It is shown
  // outside the panels, since the panel closes once the action succeeds.
  const [actionFeedback, setActionFeedback] = useState(EMPTY_FEEDBACK);
  const actionFeedbackRef = useRef(null);
  const confirmationHeadingRef = useRef(null);
  const confirmationFocusedRef = useRef(false);

  const customerCancellationOptions = useMemo(
    () => getCancellationReasonOptions("customer"),
    [],
  );

  const fetchBookingDetails = useCallback(
    async ({ skipLoader = false } = {}) => {
      if (!token) return null;
      if (!skipLoader) {
        setLoadingBooking(true);
      }
      setBookingError("");
      try {
        const { data } = await axios.get(`/api/bookings/manage/${token}`);
        setBooking(data);
        return data;
      } catch (error) {
        // A refresh after an action keeps showing what that action returned,
        // rather than swapping the page for an error.
        if (skipLoader) return null;
        const message = error.response?.data?.error || "This manage link has expired or is invalid.";
        setBookingError(message);
        setBooking(null);
        return null;
      } finally {
        if (!skipLoader) {
          setLoadingBooking(false);
        }
      }
    },
    [token],
  );

  useEffect(() => {
    if (queryAction === "reschedule" || queryAction === "cancel") {
      setActivePanel(queryAction);
    }
  }, [queryAction]);

  useEffect(() => {
    if (activePanel !== "reschedule" && activePanel !== "cancel") {
      return undefined;
    }

    let focusTimeoutId;
    const frameId = window.requestAnimationFrame(() => {
      const formNode =
        activePanel === "reschedule" ? rescheduleRef.current : cancelRef.current;
      if (!formNode) {
        return;
      }

      if (typeof formNode.scrollIntoView === "function") {
        formNode.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      const firstInput = formNode.querySelector("input, select, textarea, button");
      if (firstInput && typeof firstInput.focus === "function") {
        focusTimeoutId = window.setTimeout(() => {
          try {
            firstInput.focus({ preventScroll: true });
          } catch {
            firstInput.focus();
          }
        }, 250);
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (focusTimeoutId) {
        window.clearTimeout(focusTimeoutId);
      }
    };
  }, [activePanel]);

  useEffect(() => {
    if (!actionFeedback.message) return;
    const node = actionFeedbackRef.current;
    if (node && typeof node.focus === "function") {
      node.focus();
    }
  }, [actionFeedback]);

  useEffect(() => {
    if (!token) return;
    fetchBookingDetails();
  }, [fetchBookingDetails, token]);

  useEffect(() => {
    if (!booking?.date) {
      setRescheduleDate("");
      setRescheduleDateObj(null);
    } else {
      const nextDate = parseCalendarDay(booking.date);
      if (nextDate) {
        nextDate.setHours(0, 0, 0, 0);
        setRescheduleDateObj(nextDate);
        setRescheduleDate(toISODateString(nextDate) || "");
      }
    }
    if (booking?.timeSlot?.start && booking?.timeSlot?.end) {
      setRescheduleSlot(`${booking.timeSlot.start}-${booking.timeSlot.end}`);
    } else {
      setRescheduleSlot("");
    }
  }, [booking?.date, booking?.timeSlot?.start, booking?.timeSlot?.end]);

  // The API sends the booked service with the booking, read by id, so a
  // service hidden from the public list still shows its name and length here.
  // An API from before `bookedService` leaves it out; only then is the service
  // looked up in the public list below, which can't see hidden services.
  const apiSendsBookedService = Boolean(booking) && booking.bookedService !== undefined;

  useEffect(() => {
    if (booking?.bookedService !== undefined) {
      setServiceDetails(booking.bookedService || null);
      return;
    }
    if (booking?.service && typeof booking.service === "object" && booking.service?.title) {
      setServiceDetails(booking.service);
    }
  }, [booking?.bookedService, booking?.service]);

  useEffect(() => {
    if (booking?.professional && typeof booking.professional === "object" && booking.professional?.name) {
      setProfessionalProfile(booking.professional);
    }
  }, [booking?.professional]);

  // Times are offered at the length this booking was made at, not the service's
  // current one; bookings that never saved a length use the service's, as before.
  const bookedDurationMinutes = getBookedDurationMinutes(booking);
  const slotDurationMinutes =
    bookedDurationMinutes || serviceDetails?.duration || booking?.service?.duration;
  const bookingServiceId =
    typeof booking?.service === "object"
      ? booking.service?._id?.toString?.() || booking.service?.id?.toString?.()
      : booking?.service?.toString?.();

  useEffect(() => {
    if (!booking?.professional) return;

    const professionalId = booking.professional;
    const rangeStart = new Date();
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(rangeStart);
    rangeEnd.setDate(rangeEnd.getDate() + 90);

    const fetchSupporting = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      try {
        const slotsParams = {
          intervalMinutes: DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
          ...(bookingServiceId ? { serviceId: bookingServiceId } : {}),
          ...(bookingServiceId && bookedDurationMinutes
            ? { durationMinutes: bookedDurationMinutes }
            : {}),
        };
        const [profileRes, slotsRes, serviceRes, blockedRes] = await Promise.allSettled([
          axios.get(`/api/professional/${professionalId}`),
          axios.get(`/api/bookings/professional/${professionalId}/available-slots`, {
            params: slotsParams,
          }),
          apiSendsBookedService
            ? Promise.resolve({ data: null })
            : axios.get(`/api/services/by-user/${professionalId}`),
          axios.get(`/api/blocked-times/${professionalId}`, {
            params: {
              start: toISODateString(rangeStart),
              end: toISODateString(rangeEnd),
              view: "busy",
            },
          }),
        ]);

        if (profileRes.status === "fulfilled") {
          setProfessionalProfile(profileRes.value.data?.professional || null);
          const hours = profileRes.value.data?.availability;
          setWeeklyHours(Array.isArray(hours) ? hours : []);
        }
        if (slotsRes.status === "fulfilled") {
          setWeeklyAvailability(slotsRes.value.data || []);
        }
        if (serviceRes.status === "fulfilled" && Array.isArray(serviceRes.value.data)) {
          const selected = serviceRes.value.data.find((item) => {
            const itemId = item?._id || item?.id;
            if (!bookingServiceId || !itemId) return false;
            return itemId.toString() === bookingServiceId;
          });
          setServiceDetails(selected || null);
        }
        if (blockedRes.status === "fulfilled") {
          setBlockedTimes(Array.isArray(blockedRes.value.data) ? blockedRes.value.data : []);
        }

        const failed = [profileRes, slotsRes].some((result) => result.status === "rejected");
        if (failed) {
          setAvailabilityError("Some availability details are still loading. Please try again in a moment.");
        }
      } catch (error) {
        console.error("Error loading booking support data", error);
        setAvailabilityError("We couldn't load availability. Try again in a moment.");
      } finally {
        setAvailabilityLoading(false);
      }
    };

    fetchSupporting();
  }, [
    apiSendsBookedService,
    bookedDurationMinutes,
    booking?.professional,
    booking?.service,
    bookingServiceId,
  ]);

  const blockedByDate = useMemo(() => groupBlockedTimesByDate(blockedTimes), [blockedTimes]);

  // Which weekdays ever open: the provider's configured weekly hours, plus any
  // weekday /available-slots still has times for. That list answers for each
  // weekday's next date only, so a booked-out Monday next week is empty there;
  // on its own it would close every later Monday too. Neither knows what is
  // booked on a given date, so they only grey out days; the day's own list
  // below says which times are free.
  const weeklyPattern = useMemo(
    () => mergeWeeklyAvailability(weeklyHours, weeklyAvailability),
    [weeklyAvailability, weeklyHours],
  );

  // The times a date would have with nothing booked: the weekly pattern at the
  // booking's length, less the provider's blocked time.
  const patternSlotsForDate = useCallback(
    (isoDate) => {
      const dayAvailability = getDayAvailabilityForDate(weeklyPattern, isoDate);
      const baseSlots = slotDurationMinutes
        ? generateServiceSlotsFromAvailability(dayAvailability?.slots ?? [], {
            serviceDurationMinutes: slotDurationMinutes,
            intervalMinutes: DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
          })
        : dayAvailability?.slots ?? [];
      if (!baseSlots.length) return [];
      return filterSlotsAgainstBlocks(baseSlots, blockedByDate.get(isoDate) ?? []);
    },
    [blockedByDate, slotDurationMinutes, weeklyPattern],
  );

  const bookingDay = toISODateString(booking?.date) || "";
  const ownSlotKey =
    booking?.timeSlot?.start && booking?.timeSlot?.end ? normalizeSlotKey(booking.timeSlot) : "";

  const slotsForSelectedDate = useMemo(() => {
    if (!rescheduleDate || daySlots.date !== rescheduleDate || daySlots.error) return [];
    const listed = filterSlotsAgainstBlocks(
      daySlots.slots,
      blockedByDate.get(rescheduleDate) ?? [],
    );
    // The day's list counts this booking like any other, but its own time is
    // only taken by itself: still on offer while the pattern has room for it.
    if (
      rescheduleDate !== bookingDay ||
      !ownSlotKey ||
      listed.some((slot) => normalizeSlotKey(slot) === ownSlotKey)
    ) {
      return listed;
    }
    const ownSlot = patternSlotsForDate(rescheduleDate).find(
      (slot) => normalizeSlotKey(slot) === ownSlotKey,
    );
    if (!ownSlot) return listed;
    return [...listed, ownSlot].sort(
      (a, b) => timeStringToMinutes(a.start) - timeStringToMinutes(b.start),
    );
  }, [blockedByDate, bookingDay, daySlots, ownSlotKey, patternSlotsForDate, rescheduleDate]);

  const todayDate = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);
  const todayIso = toISODateString(todayDate);

  // The chosen day's own times, asked for while the reschedule panel is open.
  useEffect(() => {
    if (activePanel !== "reschedule" || !rescheduleDate) {
      setDaySlots(NO_DAY_SLOTS);
      setDaySlotsLoading(false);
      return undefined;
    }
    const professionalId = booking?.professional;
    if (rescheduleDate < todayIso || !professionalId || !bookingServiceId) {
      // Nothing to ask about: a day already gone (the booking's own, say) has
      // no times left.
      setDaySlots({ ...NO_DAY_SLOTS, date: rescheduleDate });
      setDaySlotsLoading(false);
      return undefined;
    }

    let active = true;
    setDaySlotsLoading(true);
    axios
      .get(`/api/bookings/professional/${professionalId}/available-slots-for-date`, {
        params: {
          date: rescheduleDate,
          serviceId: bookingServiceId,
          intervalMinutes: DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
          ...(bookedDurationMinutes ? { durationMinutes: bookedDurationMinutes } : {}),
        },
      })
      .then(({ data }) => {
        if (!active) return;
        setDaySlots({
          date: rescheduleDate,
          slots: Array.isArray(data?.slots) ? data.slots : [],
          error: "",
        });
      })
      .catch((error) => {
        console.error("Error loading times for the reschedule date", error);
        if (!active) return;
        setDaySlots({ date: rescheduleDate, slots: [], error: DAY_SLOTS_ERROR_MESSAGE });
      })
      .then(() => {
        if (active) setDaySlotsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    activePanel,
    bookedDurationMinutes,
    booking?.professional,
    bookingServiceId,
    rescheduleDate,
    todayIso,
  ]);

  const isRescheduleDateSelectable = useCallback(
    (candidate) => {
      if (!(candidate instanceof Date) || Number.isNaN(candidate.getTime())) {
        return false;
      }

      const normalizedDate = new Date(candidate);
      normalizedDate.setHours(0, 0, 0, 0);
      if (normalizedDate < todayDate) {
        return false;
      }

      const isoDate = toISODateString(normalizedDate);
      if (!isoDate) return false;

      return patternSlotsForDate(isoDate).length > 0;
    },
    [patternSlotsForDate, todayDate],
  );

  // Only the day's own answer can say a time has gone.
  const checkingDaySlots =
    Boolean(rescheduleDate) && (daySlotsLoading || daySlots.date !== rescheduleDate);

  useEffect(() => {
    if (!rescheduleSlot || checkingDaySlots) return;
    const stillVisible = slotsForSelectedDate.some((slot) => normalizeSlotKey(slot) === rescheduleSlot);
    if (!stillVisible) {
      setRescheduleSlot("");
    }
  }, [checkingDaySlots, slotsForSelectedDate, rescheduleSlot]);

  const eventStart = useMemo(
    () => combineDateAndTime(booking?.date, booking?.timeSlot?.start),
    [booking?.date, booking?.timeSlot?.start]
  );

  const eventEnd = useMemo(
    () => combineDateAndTime(booking?.date, booking?.timeSlot?.end),
    [booking?.date, booking?.timeSlot?.end]
  );

  // The start the server checks cutoffs and refund windows against: the real
  // start it sends as scheduledStartAt, read on the business's clock. Only a
  // response without one (from an older server) falls back to reading the day
  // and start time as UTC. eventStart above is a local "YYYY-MM-DDTHH:MM:00"
  // string, not a Date.
  const policyStart = useMemo(() => {
    if (booking?.scheduledStartAt) {
      const start = new Date(booking.scheduledStartAt);
      if (!Number.isNaN(start.getTime())) return start;
    }
    const wallClock = combineDateAndTime(booking?.date, booking?.timeSlot?.start);
    const start = wallClock ? new Date(`${wallClock}Z`) : null;
    return start && !Number.isNaN(start.getTime()) ? start : null;
  }, [booking?.scheduledStartAt, booking?.date, booking?.timeSlot?.start]);

  const answers = useMemo(
    () => (Array.isArray(booking?.questions) ? booking.questions : []),
    [booking?.questions]
  );

  const resolvedLocation = useMemo(() => {
    const prefersClientAddress = booking?.locationSelection === "client_location";
    const candidates = prefersClientAddress
      ? [
          booking?.clientAddress,
          booking?.location,
          booking?.address,
          booking?.professionalLocation,
          booking?.professionalBusinessAddress,
          serviceDetails?.location,
          professionalProfile?.location,
          professionalProfile?.businessAddress,
        ]
      : [
          booking?.professionalLocation,
          booking?.professionalBusinessAddress,
          booking?.location,
          booking?.address,
          serviceDetails?.location,
          professionalProfile?.location,
          professionalProfile?.businessAddress,
          booking?.clientAddress,
        ];

    // A candidate with a street wins over an earlier one with only a city or
    // ZIP, and is shown with its city, state and ZIP.
    for (const candidate of candidates) {
      if (normalizeFullAddressOnly(candidate)) return normalizeLocationString(candidate);
    }

    for (const candidate of candidates) {
      const normalized = normalizeLocationString(candidate);
      if (normalized) return normalized;
    }
    return "";
  }, [
    booking?.address,
    booking?.clientAddress,
    booking?.location,
    booking?.locationSelection,
    booking?.professionalBusinessAddress,
    booking?.professionalLocation,
    serviceDetails?.location,
    professionalProfile?.businessAddress,
    professionalProfile?.location,
  ]);

  const locationSummary = useMemo(() => {
    if (booking?.locationSelection === "client_location") {
      return resolvedLocation || "At your location";
    }
    return resolvedLocation || "Location shared after booking";
  }, [booking?.locationSelection, resolvedLocation]);

  const bookingTotal = useMemo(() => {
    const total = Number(booking?.totalPrice);
    if (Number.isFinite(total)) return total;
    const fallback = Number(serviceDetails?.price);
    if (Number.isFinite(fallback)) return fallback;
    return 0;
  }, [booking?.totalPrice, serviceDetails?.price]);

  const professionalEmail = useMemo(() => {
    if (!professionalProfile) return "";
    const candidate = professionalProfile.email || professionalProfile.contactEmail;
    return typeof candidate === "string" ? candidate.trim() : "";
  }, [professionalProfile]);

  // The address the confirmation email went to, when this page is sent it: a
  // guest's. A client who booked signed in gets theirs from their account.
  const clientEmail = useMemo(
    () =>
      (typeof booking?.customer?.email === "string" ? booking.customer.email.trim() : "") ||
      (typeof booking?.guestInfo?.email === "string" ? booking.guestInfo.email.trim() : ""),
    [booking?.customer?.email, booking?.guestInfo?.email],
  );

  const guestEmails = useMemo(() => {
    const list = [];
    if (professionalEmail) {
      list.push(professionalEmail);
    }
    if (clientEmail) {
      list.push(clientEmail);
    }
    return list;
  }, [clientEmail, professionalEmail]);

  const bookingTimeZone =
    booking?.timezone || booking?.timeZone || professionalProfile?.timezone || DEFAULT_TIME_ZONE;

  const calendarLink = useMemo(() => {
    if (!eventStart || !eventEnd) return "";

    const descriptionParts = [
      `Appointment with ${professionalProfile?.name || booking?.professional?.name || "your professional"
      }`,
    ];

    if (serviceDetails?.description) {
      descriptionParts.push(serviceDetails.description);
    }

    if (resolvedLocation) {
      descriptionParts.push(`Location: ${resolvedLocation}`);
    }

    if (booking?.notes) {
      descriptionParts.push(`Notes: ${booking.notes}`);
    }

    if (answers.length) {
      const answerLines = answers
        .map((entry) => {
          const label = entry?.label || "Question";
          const formatted = formatAnswerValue(entry?.answer) || "No response provided";
          const attachmentUrls = getBookingQuestionAnswerAttachmentsForDisplay(entry?.answer);
          const attachmentSuffix = attachmentUrls.length
            ? ` | Photos: ${attachmentUrls.join(", ")}`
            : "";
          return `${label}: ${formatted}${attachmentSuffix}`;
        })
        .filter(Boolean);
      if (answerLines.length) {
        descriptionParts.push(`Client responses:\n${answerLines.join("\n")}`);
      }
    }

    return buildGoogleCalendarLink({
      startDate: eventStart,
      endDate: eventEnd,
      title: serviceDetails?.title || "Booking",
      description: descriptionParts.join("\n\n"),
      location: resolvedLocation,
      timezone: bookingTimeZone,
      guests: guestEmails,
    });
  }, [
    answers,
    booking?.notes,
    booking?.professional?.name,
    bookingTimeZone,
    eventEnd,
    eventStart,
    professionalProfile?.name,
    resolvedLocation,
    guestEmails,
    serviceDetails?.description,
    serviceDetails?.title,
  ]);

  const handleDateChange = useCallback((selected) => {
    if (!selected) {
      setRescheduleDate("");
      setRescheduleDateObj(null);
      setRescheduleSlot("");
      return;
    }
    if (!isRescheduleDateSelectable(selected)) {
      setRescheduleDate("");
      setRescheduleDateObj(null);
      setRescheduleSlot("");
      return;
    }
    const normalized = toISODateString(selected);
    if (!normalized) {
      setRescheduleDate("");
      setRescheduleDateObj(null);
      setRescheduleSlot("");
      return;
    }
    const next = new Date(selected);
    next.setHours(0, 0, 0, 0);
    setRescheduleDate(normalized);
    setRescheduleDateObj(next);
    setRescheduleSlot("");
  }, [isRescheduleDateSelectable]);

  const canModify = useMemo(() => {
    const status = booking?.status?.toLowerCase?.();
    return status && !["cancelled", "completed", "declined"].includes(status);
  }, [booking?.status]);

  const customerActionPolicy =
    booking?.customerActionPolicy || booking?.policySnapshot || {};
  const isInsidePolicyCutoff = (cutoffMinutes) => {
    if (cutoffMinutes === null || cutoffMinutes === undefined || cutoffMinutes === "") return false;
    const cutoff = Number(cutoffMinutes);
    if (!Number.isFinite(cutoff) || cutoff < 0 || !policyStart) return false;
    return Math.floor((policyStart.getTime() - Date.now()) / 60000) < cutoff;
  };
  const cancellationCutoffReached = isInsidePolicyCutoff(
    customerActionPolicy.cancelCutoffMinutes,
  );
  const rescheduleCutoffReached = isInsidePolicyCutoff(
    customerActionPolicy.rescheduleCutoffMinutes,
  );
  const canCancel = Boolean(canModify && !cancellationCutoffReached);
  const canReschedule = Boolean(canModify && !rescheduleCutoffReached);
  const cancelNoteCharactersLeft = CANCEL_NOTE_MAX_LENGTH - cancelReasonNote.length;

  const statusLabel = statusCopy[booking?.status] || "Booked";
  const paymentLabel = paymentCopy[booking?.paymentStatus] || "Payment status unknown";
  const paymentStatusText = `${paymentLabel}${
    booking?.paymentStatus === "requires_payment" && booking?.amountDue > 0
      ? ` (${formatCurrency(booking.amountDue)} due)`
      : ""
  }`;
  const showConfirmation = arrivedFromBooking && Boolean(canModify);

  // Arriving from the booking form: start at the top, on the confirmation, and
  // move focus there so a screen reader announces that the booking went through.
  useEffect(() => {
    const heading = confirmationHeadingRef.current;
    if (!showConfirmation || !heading || confirmationFocusedRef.current) return;
    confirmationFocusedRef.current = true;
    try {
      window.scrollTo(0, 0);
    } catch (_error) {
      // Some embedded browsers throw on programmatic scrolling; ignore.
    }
    heading.focus({ preventScroll: true });
  }, [showConfirmation]);

  const resolvedCancellationPolicy = useMemo(
    () =>
      normalizeCancellationPolicy(
        customerActionPolicy.cancellationPolicy ||
          booking?.cancellationPolicy ||
          professionalProfile?.cancellationPolicy,
      ),
    [
      booking?.cancellationPolicy,
      customerActionPolicy.cancellationPolicy,
      professionalProfile?.cancellationPolicy,
    ],
  );
  const cancellationPolicyOption = useMemo(
    () => getCancellationPolicyOption(resolvedCancellationPolicy),
    [resolvedCancellationPolicy],
  );
  const cancellationOutcomePreview = useMemo(() => {
    const persistedRefundOutcome =
      booking?.customerCancellationRefund &&
      typeof booking.customerCancellationRefund === "object"
        ? booking.customerCancellationRefund
        : null;

    if (persistedRefundOutcome?.message) {
      return {
        eligible: Boolean(persistedRefundOutcome.refundEligible),
        refundStatus: normalizeRefundStatus(persistedRefundOutcome.refundStatus),
        message: persistedRefundOutcome.message,
      };
    }

    const paymentStatus = booking?.paymentStatus?.toLowerCase?.();
    if (paymentStatus !== "paid") {
      return {
        eligible: false,
        refundStatus: normalizeRefundStatus(booking?.stripeRefundStatus),
        message: "No payment was collected for this booking, so no refund is due.",
      };
    }

    const refundStatus = normalizeRefundStatus(booking?.stripeRefundStatus);
    if (refundStatus === "succeeded") {
      return {
        eligible: true,
        refundStatus,
        message: "You will receive a full refund.",
      };
    }
    if (["pending", "processing", "requires_action"].includes(refundStatus)) {
      return {
        eligible: true,
        refundStatus,
        message: "Your full refund is processing with your payment provider.",
      };
    }

    const policyEligibility = evaluateCancellationRefundEligibility({
      policy: resolvedCancellationPolicy,
      appointmentStartDateTime: policyStart,
    });

    return {
      eligible: policyEligibility.eligible,
      refundStatus,
      message: policyEligibility.message,
    };
  }, [
    booking?.customerCancellationRefund,
    booking?.paymentStatus,
    booking?.stripeRefundStatus,
    policyStart,
    resolvedCancellationPolicy,
  ]);
  // A business-initiated provider change grants a full-refund cancellation right
  // that lasts until the appointment starts; the server reports it as the
  // reason behind the refund decision.
  const providerChangedFreeCancellation = Boolean(
    canCancel && booking?.customerCancellationRefund?.reason === "provider_changed",
  );
  // Who the client booked with, as the booking page they booked on names them;
  // on a team booking that is the business, not the member taking it. An API
  // from before `bookedBusiness` leaves it out, and the provider stands in.
  const bookedBusiness = booking?.bookedBusiness?.name ? booking.bookedBusiness : null;
  const businessName = useMemo(() => {
    const candidate =
      bookedBusiness?.name || professionalProfile?.businessName || professionalProfile?.name;
    return typeof candidate === "string" ? candidate.trim() : "";
  }, [bookedBusiness?.name, professionalProfile?.businessName, professionalProfile?.name]);
  const businessAvatarUrl = resolveAvatarUrl(
    bookedBusiness ? bookedBusiness.image : professionalProfile?.image,
  );
  const notificationBusinessName = businessName || "The business";
  const manageThemeStyles = useMemo(() => {
    const backgroundColor = isHexColor(professionalProfile?.theme?.backgroundColor)
      ? professionalProfile.theme.backgroundColor
      : DEFAULT_THEME_BACKGROUND;
    const primaryTextColor = resolveReadableTextColor(
      backgroundColor,
      professionalProfile?.theme?.textColor,
    );

    return {
      "--manage-page-surface": backgroundColor,
      "--manage-brand-bg": backgroundColor,
      "--manage-brand-text": primaryTextColor,
      "--manage-brand-border": hexToRgba(primaryTextColor, 0.32),
      "--manage-focus-ring": hexToRgba(primaryTextColor, 0.24),
    };
  }, [professionalProfile?.theme?.backgroundColor, professionalProfile?.theme?.textColor]);

  const refundDetails = useMemo(() => {
    const paymentStatus = booking?.paymentStatus?.toLowerCase?.();
    const stripeRefundStatus = booking?.stripeRefundStatus?.toLowerCase?.();
    const paidAmount = Number(booking?.amountPaid);
    const hasPaidAmount = Number.isFinite(paidAmount) && paidAmount > 0;
    const refundAmountValue =
      booking?.stripeRefundAmount !== undefined && booking?.stripeRefundAmount !== null
        ? booking.stripeRefundAmount
        : hasPaidAmount
          ? booking.amountPaid
          : null;
    const refundAmount =
      refundAmountValue !== null && refundAmountValue !== undefined
        ? formatCurrency(refundAmountValue)
        : null;
    const refundDate = booking?.refundedAt ? formatDateTime(booking.refundedAt) : "";

    if (paymentStatus === "refunded") {
      const isProcessing = stripeRefundStatus === "pending" || stripeRefundStatus === "processing";
      return {
        status: isProcessing ? "processing" : "issued",
        title: isProcessing ? "Refund processing" : "Refund issued",
        description: isProcessing
          ? "Your refund is on its way back to your card. Banks can take a few days to post the credit."
          : "Your professional issued a refund for this booking.",
        amount: refundAmount,
        date: refundDate ? `${isProcessing ? "Initiated" : "Processed"} ${refundDate}` : "",
      };
    }

    if (stripeRefundStatus === "pending") {
      return {
        status: "processing",
        title: "Refund processing",
        description: "Your refund has been requested and is processing with your payment provider.",
        amount: refundAmount,
        date: refundDate ? `Initiated ${refundDate}` : "",
      };
    }

    if (paymentStatus === "paid") {
      return {
        status: cancellationOutcomePreview?.eligible ? "eligible" : "non-refundable",
        title: cancellationOutcomePreview?.eligible
          ? "Refund available"
          : "Non-refundable",
        description:
          cancellationOutcomePreview?.message ||
          "This appointment is non-refundable under the professional's cancellation policy.",
        amount: refundAmount ? `Payment on file: ${refundAmount}` : null,
        date: "",
      };
    }

    if (!hasPaidAmount || paymentStatus === "requires_payment") {
      return {
        status: "no-payment",
        title: "No payment on file",
        description: "No charges were collected for this booking, so no refund is needed.",
        amount: null,
        date: "",
      };
    }

    return null;
  }, [booking, cancellationOutcomePreview]);

  const handleReschedule = async () => {
    if (!canReschedule) {
      setRescheduleFeedback({ type: "error", message: "This booking can no longer be rescheduled." });
      return;
    }
    if (!rescheduleDate || !rescheduleSlot) {
      setRescheduleFeedback({ type: "error", message: "Please choose a new date and time." });
      return;
    }
    setIsRescheduling(true);
    setRescheduleFeedback({ type: "", message: "" });
    try {
      const [start, end] = rescheduleSlot.split("-");
      const payload = {
        date: new Date(`${rescheduleDate}T00:00:00.000Z`).toISOString(),
        timeSlot: { start, end },
      };
      const { data } = await axios.put(`/api/bookings/manage/${token}/reschedule`, payload);
      setBooking(data);
      const deliveryWarning =
        typeof data?.statusEmailWarning === "string" ? data.statusEmailWarning : "";
      setActionFeedback({
        type: deliveryWarning ? "warning" : "success",
        message: deliveryWarning
          ? `Your booking is updated. ${deliveryWarning}`
          : "Your booking is updated.",
      });
      setActivePanel("");
    } catch (error) {
      const message = error.response?.data?.error || "We couldn't reschedule this booking. Please try a different slot.";
      setRescheduleFeedback({ type: "error", message });
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleCancel = async () => {
    if (!canCancel) {
      setCancelFeedback({ type: "error", message: "This booking can no longer be cancelled." });
      return;
    }
    if (!cancelReasonCode) {
      setCancelFeedback({
        type: "error",
        message: "Please choose a cancellation reason.",
      });
      return;
    }
    if (!hasConfirmedCancel) {
      setCancelFeedback({
        type: "error",
        message: "Please confirm that you understand this action cannot be undone.",
      });
      return;
    }
    setIsCancelling(true);
    setCancelFeedback({ type: "", message: "" });
    try {
      const payload = {
        reasonCode: cancelReasonCode,
        reasonNote: cancelReasonNote.trim() || undefined,
      };
      const { data } = await axios.put(`/api/bookings/manage/${token}/cancel`, payload);
      setBooking(data);
      const cancellationMessage =
        typeof data?.customerCancellationRefund?.message === "string"
          ? data.customerCancellationRefund.message
          : "";
      const deliveryWarning =
        typeof data?.statusEmailWarning === "string" ? data.statusEmailWarning : "";
      setHasConfirmedCancel(false);
      setCancelReasonCode("");
      setCancelReasonNote("");
      await fetchBookingDetails({ skipLoader: true });
      setActionFeedback({
        type: deliveryWarning ? "warning" : "success",
        message: (
          cancellationMessage
          ? `Your booking has been cancelled. ${cancellationMessage}`
          : "Your booking has been cancelled."
        ) + (deliveryWarning ? ` ${deliveryWarning}` : ""),
      });
      setActivePanel("");
    } catch (error) {
      const message = error.response?.data?.error || "We couldn't cancel this booking right now.";
      setCancelFeedback({ type: "error", message });
    } finally {
      setIsCancelling(false);
    }
  };

  // The page belongs to the business the client booked, not to Haastia: it
  // opens with their name and picture, and Haastia gets one small credit at
  // the foot instead of its own navigation.
  const businessTopBar = businessName ? (
    <header className="booking-manage-topbar">
      <span className="booking-manage-topbar__avatar" aria-hidden="true">
        {businessAvatarUrl ? <img src={businessAvatarUrl} alt="" /> : getAvatarInitial(businessName)}
      </span>
      <span className="booking-manage-topbar__name">{businessName}</span>
    </header>
  ) : null;
  const poweredByFooter = (
    <footer className="booking-manage-footer">
      <Link to="/">Powered by Haastia</Link>
    </footer>
  );

  if (loadingBooking) {
    return (
      <div className="booking-manage-page" style={manageThemeStyles}>
        <main className="booking-manage-card">
          <p className="booking-footnote muted">Loading booking details…</p>
        </main>
        {poweredByFooter}
      </div>
    );
  }

  if (bookingError) {
    return (
      <div className="booking-manage-page" style={manageThemeStyles}>
        <main className="booking-manage-card">
          <header className="booking-header">
            <h1>Manage your booking</h1>
          </header>
          <p className="booking-footnote error">{bookingError}</p>
          <p className="booking-footnote muted">
            If this link expired, ask your professional to share a new manage link.
          </p>
        </main>
        {poweredByFooter}
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  const bookedWhen = `${formatBookingDate(booking.date)} at ${formatTimeRange(booking.timeSlot)}`;

  return (
    <div className="booking-manage-page" style={manageThemeStyles}>
      {businessTopBar}
      <main className="booking-manage-card">
        {showConfirmation ? (
          <header className="booking-header">
            <span className="booking-confirmed-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" focusable="false">
                <path
                  d="M5 12.5l4.5 4.5L19 7.5"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <h1 ref={confirmationHeadingRef} tabIndex={-1}>
              {businessName ? `You’re booked with ${businessName}` : "You’re booked"}
            </h1>
            <p className="booking-confirmed-when">
              {[serviceDetails?.title, bookedWhen].filter(Boolean).join(" · ")}
            </p>
            <p className="booking-confirmed-payment">
              {describePaidAndDue(booking, bookingTotal) || paymentStatusText}
            </p>
            <p className="booking-lede">
              {clientEmail ? (
                <>
                  We’ve emailed a confirmation to <strong>{clientEmail}</strong>.
                </>
              ) : booking.customer ? (
                "We’ve emailed a confirmation to the address on your account."
              ) : (
                "We’ve emailed you a confirmation."
              )}{" "}
              Its link brings you back here if you need to reschedule or cancel.
            </p>
          </header>
        ) : (
          <header className="booking-header">
            <span className="booking-badge">Self-serve tools</span>
            <h1>Manage your booking</h1>
            <p className="booking-lede">
              Use this secure link to reschedule or cancel without logging in. {notificationBusinessName} will be notified instantly.
            </p>
          </header>
        )}

        {providerChangedFreeCancellation && (
          <p
            className="manage-provider-changed-notice"
            data-testid="provider-changed-free-cancellation"
          >
            Your provider was changed by {notificationBusinessName}. You can cancel for a
            full refund until your appointment starts.
          </p>
        )}

        <section className="booking-manage-summary">
          <div>
            <p className="summary-label">Service</p>
            <h2>{serviceDetails?.title || "Reserved service"}</h2>
            <p className="summary-provider">{professionalProfile?.name || "Your professional"}</p>
          </div>
          <div className="summary-meta">
            <div>
              <span>Status</span>
              <strong>{statusLabel}</strong>
            </div>
            <div>
              <span>When</span>
              <strong>{bookedWhen}</strong>
              {bookingTimeZoneNotice(bookingTimeZone) && (
                <small className="summary-time-zone">{bookingTimeZoneNotice(bookingTimeZone)}</small>
              )}
            </div>
            <div>
              <span>Payment</span>
              <strong>{paymentStatusText}</strong>
            </div>
            <div>
              <span>Service total</span>
              <strong>{formatCurrency(bookingTotal)}</strong>
            </div>
            <div>
              <span>Location</span>
              <strong>{locationSummary}</strong>
            </div>
          </div>
        </section>

        <div className="booking-manage-actions">
          {calendarLink && (
            <a
              className="calendar-button secondary"
              href={calendarLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Add to Google Calendar <span aria-hidden="true">↗</span>
            </a>
          )}
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setActionFeedback(EMPTY_FEEDBACK);
              setActivePanel((current) => (current === "reschedule" ? "" : "reschedule"));
            }}
            disabled={!canReschedule}
          >
            Reschedule
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => {
              setActionFeedback(EMPTY_FEEDBACK);
              setActivePanel((current) => (current === "cancel" ? "" : "cancel"));
            }}
            disabled={!canCancel}
          >
            Cancel booking
          </button>
        </div>
        {!canModify && (
          <p className="booking-footnote muted">
            This booking is {statusLabel.toLowerCase()}. Self-serve actions are disabled.
          </p>
        )}
        {canModify && rescheduleCutoffReached && (
          <p className="booking-footnote muted">The rescheduling cutoff for this booking has passed.</p>
        )}
        {canModify && cancellationCutoffReached && (
          <p className="booking-footnote muted">The cancellation cutoff for this booking has passed.</p>
        )}
        {actionFeedback.message && (
          <p
            ref={actionFeedbackRef}
            className={`booking-feedback manage-action-feedback ${actionFeedback.type}`}
            role="status"
            tabIndex={-1}
          >
            {actionFeedback.message}
          </p>
        )}

        {activePanel === "reschedule" && (
          <section className="manage-panel is-active" ref={rescheduleRef}>
            <div className="panel-header">
              <div>
                <p className="summary-label">Reschedule</p>
                <h2>Pick a new date and time</h2>
              </div>
              {availabilityLoading && <span className="loading-dot">Syncing availability.</span>}
            </div>
            {availabilityError && <p className="booking-footnote warning">{availabilityError}</p>}
            <div className="reschedule-grid">
              <div className="booking-field">
                <label htmlFor="reschedule-date">
                  Date <span aria-hidden="true">*</span>
                </label>
                <div className="booking-input-shell">
                  <DatePicker
                    id="reschedule-date"
                    ariaRequired="true"
                    selected={rescheduleDateObj}
                    onChange={handleDateChange}
                    minDate={todayDate}
                    filterDate={isRescheduleDateSelectable}
                    placeholderText="Select a date"
                    dateFormat="MMMM d, yyyy"
                    className="booking-date-input"
                  />
                </div>
              </div>
              <div className="booking-field">
                <label id="reschedule-time-label">
                  Time <span aria-hidden="true">*</span>
                </label>
                <div className="slot-grid" role="group" aria-labelledby="reschedule-time-label">
                  {rescheduleDate && slotsForSelectedDate.length > 0 ? (
                    slotsForSelectedDate.map((slot) => {
                      const key = normalizeSlotKey(slot);
                      const isSelected = rescheduleSlot === key;
                      return (
                        <button
                          type="button"
                          key={key}
                          className={`slot-pill ${isSelected ? "is-selected" : ""}`}
                          onClick={() => setRescheduleSlot(key)}
                          aria-pressed={isSelected}
                        >
                          {formatSlotForDisplay(slot)}
                        </button>
                      );
                    })
                  ) : (
                    <p
                      className={`booking-footnote ${
                        daySlots.error && !checkingDaySlots ? "warning" : "muted"
                      }`}
                    >
                      {!rescheduleDate
                        ? "Select a date to see available times."
                        : checkingDaySlots
                          ? "Checking times…"
                          : daySlots.error || "No open slots this day. Please choose another date."}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {rescheduleFeedback.message && (
              <p className={`booking-feedback ${rescheduleFeedback.type || ""}`} role="alert">
                {rescheduleFeedback.message}
              </p>
            )}
            <div className="panel-actions">
              <button type="button" className="primary" onClick={handleReschedule} disabled={!canReschedule || isRescheduling}>
                {isRescheduling ? "Updating…" : "Confirm new time"}
              </button>
              <button type="button" className="ghost" onClick={() => setActivePanel("")}>
                Back to summary
              </button>
            </div>
          </section>
        )}
        {activePanel === "cancel" && (
          <section className="manage-panel is-active" ref={cancelRef}>
            <div className="panel-header">
              <div>
                <p className="summary-label">Cancel booking</p>
                <h2>Are you sure?</h2>
              </div>
            </div>
            <p className="booking-lede">
              Cancelling will immediately release this time on your professional&apos;s schedule.
              Please choose a reason so they can plan accordingly.
            </p>
            <div className="refund-policy-card">
              <p className="summary-label">Cancellation policy</p>
              <p className="refund-policy-copy">
                {cancellationPolicyOption.description}
              </p>
            </div>
            {refundDetails && (
              <div className="refund-status-card">
                <div className="refund-status-header">
                  <p className="summary-label">Refunds</p>
                  <span className={`refund-pill ${refundDetails.status}`}>{refundDetails.title}</span>
                </div>
                <p className="refund-status-copy">{refundDetails.description}</p>
                <div className="refund-meta-grid">
                  {refundDetails.amount && (
                    <p className="refund-meta">
                      <span>Amount</span>
                      <strong>{refundDetails.amount}</strong>
                    </p>
                  )}
                  {refundDetails.date && <p className="refund-meta muted-meta">{refundDetails.date}</p>}
                </div>
              </div>
            )}
            <div className="booking-field">
              <label htmlFor="cancel-reason-code">
                Cancellation reason <span>*</span>
              </label>
              <select
                id="cancel-reason-code"
                className="booking-select"
                value={cancelReasonCode}
                onChange={(event) => setCancelReasonCode(event.target.value)}
              >
                <option value="">Select a reason</option>
                {customerCancellationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="booking-field">
              <label htmlFor="cancel-reason-note">Additional note (optional)</label>
              <textarea
                id="cancel-reason-note"
                rows={3}
                className="booking-textarea"
                placeholder="Share a quick reason (optional)"
                maxLength={CANCEL_NOTE_MAX_LENGTH}
                aria-describedby="cancel-reason-note-count"
                value={cancelReasonNote}
                onChange={(event) => setCancelReasonNote(event.target.value)}
              />
              <p id="cancel-reason-note-count" className="booking-footnote muted">
                {cancelNoteCharactersLeft}{" "}
                {cancelNoteCharactersLeft === 1 ? "character" : "characters"} left
              </p>
            </div>
            <label className="confirm-checkbox">
              <input
                type="checkbox"
                checked={hasConfirmedCancel}
                onChange={(event) => setHasConfirmedCancel(event.target.checked)}
              />
              I understand this booking will be cancelled now, and refunds follow the professional&apos;s cancellation policy.
            </label>
            {cancelFeedback.message && (
              <p className={`booking-feedback ${cancelFeedback.type || ""}`} role="alert">
                {cancelFeedback.message}
              </p>
            )}
            <div className="panel-actions">
              <button type="button" className="danger" onClick={handleCancel} disabled={!canCancel || isCancelling}>
                {isCancelling ? "Cancelling…" : "Cancel booking"}
              </button>
              <button type="button" className="ghost" onClick={() => setActivePanel("")}>
                Keep booking
              </button>
            </div>
          </section>
        )}
      </main>
      {poweredByFooter}
    </div>
  );
};

export default BookingManagePage;
